import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Divider, CircularProgress, Slider, Card, CardContent, Chip, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import { generateRoastByBedrockAWS, getPhysicianExpertises, getPhysicianTrials, generateRoast, getNpiDkwsFromSimilarity } from '../api';
import Waitinglist from './Waitinglist';

const PhysicianHeader = styled(Box)(({ theme }) => ({
  background: '#1EB0BC',
  color: '#fff',
  padding: theme.spacing(4, 2, 2, 2),
  // borderTopLeftRadius: theme.spacing(2),
  // borderTopRightRadius: theme.spacing(2),
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0px 4px 24px rgba(23, 97, 160, 0.10)',
}));

const StatCard = styled(Box)(({ theme }) => ({
  background: '#f8fafc',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2, 3),
  minWidth: 120,
  textAlign: 'center',
  boxShadow: '0 2px 8px 0 rgba(23,97,160,0.05)',
  margin: theme.spacing(1, 0),
}));

const Pill = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
  fontSize: '0.98rem',
  background: '#e3f5f7',
  color: '#1761a0',
}));

const RoastButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#e57373',
  '&:hover': {
    backgroundColor: '#ef5350',
  },
  borderRadius: '20px',
  padding: '8px 16px',
  marginRight: theme.spacing(2),
}));

const ShareButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#e57373',
  '&:hover': {
    backgroundColor: '#ef5350',
  },
  borderRadius: '4px',
  padding: '8px 16px',
}));

const PhysicianRoast = ({ currentPhysician }) => {
  const [llmRes, setLlmRes] = useState("Click 0.7");
  const [loading, setLoading] = useState(false);
  const [expertiseData, setExpertiseData] = useState([]);
  const [trialsData, setTrialsData] = useState([]);
  const [npiDkws, setNpiDkws] = useState([]);
  const [roastLevel, setRoastLevel] = useState(0.7); // Default to medium level (0.7)
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);

  // Fetch expertise and trials data when physician changes
  useEffect(() => {
    if (currentPhysician && currentPhysician.npi) {
      const fetchData = async () => {
        try {
          const expertise = await getPhysicianExpertises(currentPhysician.npi);
          const trials = await getPhysicianTrials(currentPhysician.npi);
          const npiDkwsData = await getNpiDkwsFromSimilarity(currentPhysician.npi);  
          setExpertiseData(expertise || []);
          setTrialsData(trials || []);
          setNpiDkws(npiDkwsData.npiFromSimilarity|| []);
        } catch (error) {
          console.error("Error fetching physician data:", error);
        }
      };
      const handleGenerateRoast = async () => {
        if (!currentPhysician) return;
        setLoading(true);
        try {
          const llmRes = await generateRoast(currentPhysician, expertiseData, trialsData, 0.7);
          setLlmRes(llmRes);
        } catch (error) {
          console.error("Error generating roast:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
      handleGenerateRoast();
    }
  }, [currentPhysician]);

  const handleShareOnTwitter = () => {
    // Get the current roast text based on the slider position
    const currentRoastText = "getCurrentRoastText()";
    const tweetText = encodeURIComponent(`${currentRoastText} #PhysicianRoast #LinkMedicine`);
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  // Get the current roast text based on the slider position
  // const getCurrentRoastText = () => {
  //   switch(roastLevel) {
  //     case 0: return roastText5;
  //     case 1: return roastText7;
  //     case 2: return roastText9;
  //     default: return roastText7;
  //   }
  // };

  // Handle slider change
  const handleSliderChange = (event, newValue) => {
    setRoastLevel(newValue);
  };

  if (!currentPhysician) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography variant="h6">Select a physician to view details.</Typography>
      </Box>
    );
  }
  // Helper values
  const fullName = `${currentPhysician.frst_nm || ''} ${currentPhysician.lst_nm || ''}${currentPhysician.cred ? ', ' + currentPhysician.cred : ''}`.trim();
  const location = [currentPhysician.cty, currentPhysician.st].filter(Boolean).join(', ');
  // Years of practice
  const yearsOfPractice = currentPhysician.grd_yr ? `${Math.max(2025 - parseInt(currentPhysician.grd_yr, 10), 0)}+` : '—';
  // Disease expertise count
  const diseaseExpertise = expertiseData && expertiseData.length ? `${expertiseData.length}+` : '—';
  // Searches (mock or from data)
  const searchCount = currentPhysician.search_count || '—';
  // Tag pills
  const npiDkwsTags = npiDkws && npiDkws.length ? npiDkws.slice(0, 8).map((d, i) => <Pill key={i} label={d} />) : [];

  return (
    <>
    <Box mx="auto">
      {/* Header */}
      <PhysicianHeader>
        <Typography variant="h4" fontWeight={700}>{fullName}</Typography>
        {location && (
          <Typography variant="h6" sx={{ mt: 1, opacity: 0.85 }}>{location + (currentPhysician.org_nm ? ' | ' + currentPhysician.org_nm : '') }</Typography>
        )}
        <Typography sx={{ mt: 1, opacity: 0.85 }}>{llmRes.tagline ? '"' + llmRes.tagline + '"' : ''}</Typography>
      </PhysicianHeader>
    </Box>  
    <Box maxWidth={860} mx="auto">
      {/* Know Your Doc Card */}
      <SectionCard>
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="h6" fontWeight={700} component="div">
              Know Your Doc, With a Smile <span role="img" aria-label="smile">😊</span>
            </Typography>
          </Box>
          <Box display="flex" alignItems="flex-start" bgcolor="#e3f5f7" borderRadius={2} p={2} mb={2}>
            <EmojiObjectsIcon sx={{ color: '#1761a0', mr: 1, mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              <b>AI-Generated Insight:</b> We asked AI to analyze the public digital footprint of your doctor—including publications, hospital bios, and clinical records—to generate this playful and informative summary.
            </Typography>
          </Box>
          {/* Roast slider and controls */}
          
          {/* <Box display="flex" alignItems="center" mb={2}>
            <RoastButton 
              onClick={handleGenerateRoast} 
              disabled={loading || !currentPhysician}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LocalFireDepartmentIcon />}
            >
              {loading ? 'Generating...' : 'Generate Roast'}
            </RoastButton>
            <ShareButton 
              startIcon={<TwitterIcon />} 
              onClick={handleShareOnTwitter}
              disabled={getCurrentRoastText().includes('Click') || loading}
            >
              Share
            </ShareButton>
            <Box flexGrow={1} ml={2}>
              <Slider
                value={roastLevel}
                onChange={handleSliderChange}
                step={1}
                marks={[
                  { value: 0, label: "Mild" },
                  { value: 1, label: "Medium" },
                  { value: 2, label: "Spicy" }
                ]}
                min={0}
                max={2}
                disabled={loading || getCurrentRoastText().includes('Click')}
                sx={{
                  color: '#e57373',
                  maxWidth: 220,
                  ml: 2,
                  '& .MuiSlider-thumb': {
                    height: 24,
                    width: 24,
                  },
                }}
              />
            </Box>
          </Box> */}
          
          {/* Roast output */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" sx={{ my: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              // <Box sx={{ whiteSpace: 'pre-line' }}>{getCurrentRoastText()}</Box>
              <Box sx={{ whiteSpace: 'pre-line' }}>
              <b>{llmRes.tagline}</b>
              <br />
              <br />
              {llmRes.roast}
              </Box>
            )}
          </Typography>
        </CardContent>
      </SectionCard>

      {/* Clinical Capability Card */}
      <SectionCard>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700} component="div">
              Clinical Capability <span role="img" aria-label="wave">🩺</span>
            </Typography>
          </Box>
          <Box display="flex" alignItems="flex-start" bgcolor="#e3f5f7" borderRadius={2} p={2} mb={2}>
            <EmojiObjectsIcon sx={{ color: '#1761a0', mr: 1, mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              <b>AI-Generated Insight:</b> We asked AI to analyze the public digital footprint of your doctor—including publications, hospital bios, and clinical records—to generate this playful and informative summary.
            </Typography>
          </Box>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  🧬 Years of Practice
                </Typography>
                <Typography variant="h6" fontWeight={700}>{yearsOfPractice}</Typography>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                📘 Disease Expertise
                </Typography>
                <Typography variant="h6" fontWeight={700}>{npiDkws.length}</Typography>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                👁️‍🗨️ Searches
                </Typography>
                <Typography variant="h6" fontWeight={700}>{searchCount|1382}</Typography>
              </StatCard>
            </Grid>
          </Grid>
          <Box display="flex" flexWrap="wrap" mb={2}>
            {npiDkwsTags}
          </Box>
          <Typography variant="body1" sx={{ my: 2 }}>
          <Box sx={{ whiteSpace: 'pre-line' }}>
            {llmRes.summary}
            </Box>
          </Typography>
          <Button variant="contained" color="primary" fullWidth sx={{ mt: 2, borderRadius: 2 }} onClick={() => setShowWaitlistDialog(true)}>
            Join the Waiting List of Full Profile
          </Button>
          <Waitinglist
            open={showWaitlistDialog}
            onClose={() => setShowWaitlistDialog(false)}
            onSubmit={(email) => {
              // TODO: handle email submission (API call, etc)
              setShowWaitlistDialog(false);
            }}
          />
        </CardContent>
      </SectionCard>

      {/* Care Compatibility */}
      <SectionCard>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700} component="div">
              Care Compatibility <span role="img" aria-label="wave">❤️</span>
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ my: 2 }}>
          <Box sx={{ whiteSpace: 'pre-line' }}>{llmRes.careCompatibility}</Box>
          </Typography>
        </CardContent>
      </SectionCard>

      {/* AI-Powered Roast */}
      <SectionCard>
        <CardContent sx={{ backgroundColor: '#fff8e1'}}>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700} component="div">
            AI-Powered Roast <span role="img" aria-label="wave">🤖</span>
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ my: 2 }}>
            <Box sx={{ whiteSpace: 'pre-line' }}>{llmRes.spicyRoast}
            <br />
            <Typography variant="body2" sx={{ my: 2 }}><i>⚠️ Warning: Contains high doses of humor and excellence.</i></Typography>

            </Box>
          </Typography>
        </CardContent>
      </SectionCard>
    </Box>
    </> 
  );
};

export default PhysicianRoast;